import type { AirportOption } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { airportOptionsOptions } from "../../lib/api/airports";
import { regionName } from "../../lib/format/region";
import { airportLabel, airportSearchTerm } from "../../lib/travel/airports";

/** Typing pauses this long before the airports are asked (platform rule: never a request per keystroke). */
const DEBOUNCE_MS = 300;

export interface AirportSelectProps {
	id: string;
	/** The text in the field — free text; an airport is only a shortcut to it. */
	value: string;
	/** Every change of the text: typed, or `City (IATA)` written by a pick. */
	onChange: (text: string) => void;
	/** After a pick (the label is already written through `onChange`). */
	onPick?: (airport: AirportOption) => void;
	/** The field's own limit; a picked label is shortened to fit it. */
	maxLength?: number;
	placeholder?: string;
	invalid?: boolean;
	disabled?: boolean;
	/** The input's classes — the caller's field style (the leg form's tall input, a settings form's small one). */
	className: string;
	"aria-describedby"?: string;
}

/**
 * A text field that suggests airports (`GET /airports/options`) while you
 * type, and keeps whatever you type when none fits — a land crossing, a
 * ferry port, a place the list does not know. Picking one writes
 * `City (IATA)` into the text. Options read `City · IATA`, then the airport's
 * name and country, in the server's order (large airports first).
 *
 * Asked only after a pause (DEBOUNCE_MS) and from AIRPORT_OPTIONS_Q_MIN
 * characters; answers are cached per term. The list is portalled with fixed
 * positioning into the nearest open <dialog> (else the body), like the
 * platform Select, so a modal's scrolling body never clips it.
 */
export function AirportSelect({ id, value, onChange, onPick, maxLength, placeholder, invalid, disabled, className, ...rest }: AirportSelectProps) {
	const { t } = useTranslation("travel");
	const listId = useId();
	const input = useRef<HTMLInputElement>(null);
	const popover = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(0);

	const term = airportSearchTerm(value);
	const [debounced, setDebounced] = useState(term);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(term), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [term]);

	const waiting = term !== debounced;
	const results = useQuery({ ...airportOptionsOptions(debounced ?? ""), enabled: open && !!debounced && !disabled });
	const items = !waiting && debounced ? (results.data?.items ?? []) : [];
	const showList = open && !!term && !disabled;

	useEffect(() => setActive(0), [debounced]);

	// Fixed position under (or above) the field; follows scrolls of any ancestor and resizes.
	const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
	useLayoutEffect(() => {
		if (!showList) {
			setPos(null);
			return;
		}
		const place = () => {
			const r = input.current?.getBoundingClientRect();
			if (!r) return;
			const below = window.innerHeight - r.bottom;
			const up = below < 300 && r.top > below;
			setPos({ top: up ? r.top : r.bottom, left: r.left, width: r.width, up });
		};
		place();
		window.addEventListener("resize", place);
		document.addEventListener("scroll", place, true);
		return () => {
			window.removeEventListener("resize", place);
			document.removeEventListener("scroll", place, true);
		};
	}, [showList]);

	useEffect(() => {
		if (!showList) return;
		popover.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
	}, [active, showList]);

	const pick = (a: AirportOption) => {
		onChange(airportLabel(a, maxLength));
		onPick?.(a);
		setOpen(false);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				if (!open) setOpen(true);
				else setActive((i) => Math.min(Math.max(0, items.length - 1), i + 1));
				break;
			case "ArrowUp":
				if (!showList) return;
				e.preventDefault();
				setActive((i) => Math.max(0, i - 1));
				break;
			case "Enter": {
				// Only a highlighted airport takes Enter; otherwise the field behaves as before.
				const a = showList ? items[active] : undefined;
				if (!a) return;
				e.preventDefault();
				pick(a);
				break;
			}
			case "Escape":
				if (!showList) return;
				// Close the list, not the dialog around it.
				e.preventDefault();
				e.stopPropagation();
				setOpen(false);
				break;
		}
	};

	const status = waiting || (results.isFetching && !results.data) ? t("airportPicker.searching") : results.isError ? t("airportPicker.failed") : items.length === 0 ? t("airportPicker.noMatch") : null;

	return (
		<>
			<input
				ref={input}
				id={id}
				type="text"
				role="combobox"
				aria-autocomplete="list"
				aria-expanded={showList}
				aria-controls={showList ? listId : undefined}
				aria-activedescendant={showList && items[active] ? `${listId}-${active}` : undefined}
				aria-invalid={invalid || undefined}
				aria-describedby={rest["aria-describedby"] ?? (invalid ? `${id}-error` : undefined)}
				value={value}
				maxLength={maxLength}
				placeholder={placeholder}
				disabled={disabled}
				autoComplete="off"
				spellCheck={false}
				className={className}
				onChange={(e) => {
					onChange(e.target.value);
					setOpen(true);
				}}
				onKeyDown={onKeyDown}
				onBlur={() => setOpen(false)}
			/>
			{showList &&
				pos &&
				createPortal(
					<div
						ref={popover}
						style={{ left: pos.left, width: Math.max(pos.width, 256), ...(pos.up ? { bottom: window.innerHeight - pos.top + 4 } : { top: pos.top + 4 }) }}
						className="fixed z-30 overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-[0_8px_24px_rgba(16,24,40,0.12)]"
					>
						{status !== null ? (
							<p role="status" className="px-[0.75rem] py-[0.625rem] text-[0.8125rem] text-gms-ink-3">
								{status}
							</p>
						) : (
							<ul id={listId} role="listbox" aria-label={t("airportPicker.listLabel")} className="max-h-[17rem] overflow-y-auto p-[0.25rem] [scrollbar-width:thin]">
								{items.map((a, i) => (
									<li
										key={a.iata_code}
										id={`${listId}-${i}`}
										role="option"
										aria-selected={i === active}
										data-index={i}
										onMouseEnter={() => setActive(i)}
										// Keep the focus in the field: a blur would close the list before the click lands.
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => pick(a)}
										className={`grid cursor-pointer gap-[0.0625rem] rounded-[0.375rem] px-[0.625rem] py-[0.4375rem] ${i === active ? "bg-base-200" : ""}`}
									>
										<span className="flex min-w-0 items-baseline gap-[0.375rem] text-[0.875rem]">
											<span className="truncate font-semibold text-gms-navy-ink">{a.city || a.name}</span>
											<span aria-hidden className="text-gms-ink-3">
												·
											</span>
											<span className="font-mono text-[0.8125rem] font-semibold tracking-[0.06em] text-gms-navy">{a.iata_code}</span>
										</span>
										<span className="truncate text-[0.75rem] text-gms-ink-3">
											{a.name}
											<span aria-hidden> · </span>
											{regionName(a.country_code)}
										</span>
									</li>
								))}
							</ul>
						)}
					</div>,
					input.current?.closest("dialog") ?? document.body,
				)}
		</>
	);
}
